import { CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

export const formatQbDate = (d?: string | null) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "—";
  return `${dt.getMonth() + 1}/${dt.getDate()}/${String(dt.getFullYear()).slice(-2)}`;
};

export const fmtCurrency = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(n) || 0);

const daysUntil = (d?: string | null) => {
  if (!d) return null;
  const due = new Date(d);
  const today = new Date();
  due.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return Math.round((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
};

export type QbStatus = {
  primary: string;
  secondary?: string | null;
  tone: "paid" | "due" | "overdue" | "neutral" | "draft";
};

export const getQbStatus = (inv: any): QbStatus => {
  const status = inv.status as string;
  const total = Number(inv.total) || 0;
  const paid = Number(inv.amount_paid) || 0;
  const remaining = total - paid;

  if (status === "paid") {
    return { primary: "Paid", secondary: inv.paid_at ? formatQbDate(inv.paid_at) : null, tone: "paid" };
  }
  if (status === "partially_paid") {
    return { primary: "Partially paid", secondary: `${fmtCurrency(remaining)} due`, tone: "due" };
  }
  if (status === "overdue") {
    return { primary: "Overdue", secondary: inv.sent_at ? `Sent ${formatQbDate(inv.sent_at)}` : null, tone: "overdue" };
  }
  if (status === "sent") {
    const days = daysUntil(inv.due_date);
    const primary =
      days === null ? "Sent" : days < 0 ? `Overdue ${Math.abs(days)}d` : days === 0 ? "Due today" : `Due in ${days} days`;
    return { primary, secondary: inv.sent_at ? `Sent ${formatQbDate(inv.sent_at)}` : null, tone: days !== null && days < 0 ? "overdue" : "due" };
  }
  if (status === "approved") return { primary: "Ready to send", tone: "neutral" };
  if (status === "draft") return { primary: "Draft", tone: "draft" };
  if (status === "void") return { primary: "Void", tone: "neutral" };
  return { primary: status, tone: "neutral" };
};

export const QbStatusCell = ({ inv }: { inv: any }) => {
  const s = getQbStatus(inv);
  const toneClass =
    s.tone === "paid" ? "text-green-700 dark:text-green-400"
    : s.tone === "overdue" ? "text-destructive"
    : s.tone === "due" ? "text-foreground"
    : "text-muted-foreground";
  return (
    <div className="flex items-start gap-1.5 leading-tight">
      {s.tone === "paid" && <CheckCircle2 className="h-4 w-4 mt-0.5 text-green-600 shrink-0" />}
      <div className="min-w-0">
        <div className={cn("text-sm font-medium", toneClass)}>{s.primary}</div>
        {s.secondary && <div className="text-xs text-muted-foreground">{s.secondary}</div>}
      </div>
    </div>
  );
};

export type QbActionHandlers = {
  onEdit?: () => void;
  onApprove?: () => void;
  onSend?: () => void;
  onReceivePayment?: () => void;
  onPrint?: () => void;
  busy?: boolean;
};

export const QbActionLinks = ({ inv, handlers }: { inv: any; handlers: QbActionHandlers }) => {
  const status = inv.status as string;
  const isQb = inv.source === "qb_import";
  const links: { label: string; onClick?: () => void }[] = [];

  if (handlers.onEdit) links.push({ label: "View/Edit", onClick: handlers.onEdit });
  if (status === "draft" && handlers.onApprove) links.push({ label: "Approve", onClick: handlers.onApprove });
  if (status === "approved" && handlers.onSend) links.push({ label: "Mark as Sent", onClick: handlers.onSend });
  if (!isQb && status === "approved" && handlers.onReceivePayment) links.push({ label: "Receive payment", onClick: handlers.onReceivePayment });
  if (!isQb && ["sent", "partially_paid", "overdue"].includes(status) && handlers.onReceivePayment) {
    links.push({ label: "Receive payment", onClick: handlers.onReceivePayment });
  }
  if (["sent", "partially_paid", "overdue", "paid"].includes(status) && handlers.onPrint) {
    links.push({ label: "Print", onClick: handlers.onPrint });
  }

  return (
    <div className="flex items-center gap-1.5 flex-wrap" onClick={(e) => e.stopPropagation()}>
      {links.map((l, i) => (
        <span key={l.label} className="flex items-center gap-1.5">
          {i > 0 && <span className="text-muted-foreground/50">·</span>}
          <button
            type="button"
            disabled={handlers.busy}
            onClick={l.onClick}
            className="text-sm text-primary hover:underline disabled:opacity-50 disabled:pointer-events-none"
          >
            {l.label}
          </button>
        </span>
      ))}
    </div>
  );
};

export const QbTypeBadge = ({ label = "Invoice", isQb = false }: { label?: string; isQb?: boolean }) => (
  <Badge variant="outline" className="font-normal text-xs h-5 px-1.5">
    {isQb ? "QB" : label}
  </Badge>
);
